import { createTheme } from '@mui/material/styles';
import { defaultTheme } from 'react-admin';

const getSidebarOverrides = (baseSidebar) => ({
    ...baseSidebar,
    styleOverrides: {
        ...baseSidebar?.styleOverrides,
        root: {
            ...baseSidebar?.styleOverrides?.root,
            'width': 264,
            'transition': 'width 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&.RaSidebar-fixed': {
                width: 264,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            },
            '&.RaSidebar-closed': {
                'width': 64,
                '&.RaSidebar-fixed': {
                    width: 64,
                },
            },
        },
        paper: {
            ...baseSidebar?.styleOverrides?.paper,
            borderRight: '1px solid var(--divider-color)',
            boxShadow: 'none',
        },
    },
});

const getToolbarOverrides = (baseToolbar) => ({
    ...baseToolbar,
    styleOverrides: {
        ...baseToolbar?.styleOverrides,
        root: {
            ...baseToolbar?.styleOverrides?.root,
            'backgroundColor': 'var(--background-paper)',
            'borderTop': '1px solid var(--divider-color)',
            '& .MuiButton-root': {
                marginRight: '8px',
            },
        },
    },
});

const getGridDefaultProps = (baseGridProps) => ({
    ...baseGridProps,
    spacing: baseGridProps?.spacing ?? 2,
    columnSpacing: baseGridProps?.columnSpacing ?? 2,
    rowSpacing: baseGridProps?.rowSpacing ?? 2,
});

const getSearchInputOverrides = (baseSearchInput) => ({
    ...baseSearchInput,
    styleOverrides: {
        ...baseSearchInput?.styleOverrides,
        root: {
            ...baseSearchInput?.styleOverrides?.root,
            '& .MuiInputBase-root': {
                minHeight: 48,
                height: 48,
                boxSizing: 'border-box',
            },
        },
    },
});

const getFilterFormOverrides = (baseFilterForm) => ({
    ...baseFilterForm,
    styleOverrides: {
        ...baseFilterForm?.styleOverrides,
        root: {
            ...baseFilterForm?.styleOverrides?.root,
            width: '100%',
            flexWrap: 'wrap',
            '& .RaFilterForm-filterFormInput:has(.applied-filters-container)': {
                flex: '1 0 100%',
                flexBasis: '100%',
                width: '100%',
            },
            '& .filter-field:has(.applied-filters-container)': {
                flex: '1 0 100%',
                flexBasis: '100%',
                width: '100%',
            },
        },
    },
});

const getFilterFormInputOverrides = (baseFilterFormInput) => ({
    ...baseFilterFormInput,
    styleOverrides: {
        ...baseFilterFormInput?.styleOverrides,
        root: {
            ...baseFilterFormInput?.styleOverrides?.root,
            '&:has(.applied-filters-container)': {
                flex: '1 0 100%',
                flexBasis: '100%',
                width: '100%',
            },
        },
    },
});

const addGridSpacing = (baseTheme) => {
    const baseComponents = baseTheme.components || {};
    const baseMuiGrid = baseComponents.MuiGrid || {};
    const baseMuiGrid2 = baseComponents.MuiGrid2 || baseMuiGrid;

    return createTheme({
        ...baseTheme,
        components: {
            ...baseComponents,
            MuiCssBaseline: {
                styleOverrides: (theme) => ({
                    ':root': {
                        '--text-primary': theme.palette.text.primary,
                        '--text-secondary': theme.palette.text.secondary,
                        '--divider-color': theme.palette.divider,
                        '--background-default': theme.palette.background.default,
                        '--background-paper': theme.palette.background.paper,
                        '--primary-main': theme.palette.primary.main,
                    },
                }),
            },
            RaSidebar: getSidebarOverrides(baseComponents.RaSidebar || {}),
            RaToolbar: getToolbarOverrides(baseComponents.RaToolbar || {}),
            RaSearchInput: getSearchInputOverrides(baseComponents.RaSearchInput || {}),
            RaFilterForm: getFilterFormOverrides(baseComponents.RaFilterForm || {}),
            RaFilterFormInput: getFilterFormInputOverrides(baseComponents.RaFilterFormInput || {}),
            MuiTableSortLabel: {
                styleOverrides: {
                    root: {
                        flexDirection: 'row !important',
                    },
                },
            },
            MuiGrid: {
                ...baseMuiGrid,
                defaultProps: getGridDefaultProps(baseMuiGrid.defaultProps || {}),
            },
            MuiGrid2: {
                ...baseMuiGrid2,
                defaultProps: getGridDefaultProps(baseMuiGrid2.defaultProps || {}),
            },
        },
    });
};

export const lightTheme = addGridSpacing(defaultTheme);

export const darkTheme = addGridSpacing({
    ...defaultTheme,
    palette: {
        ...defaultTheme.palette,
        mode: 'dark',
        primary: {
            main: '#90caf9', // Softer blue for dark mode
        },
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
    },
});

// Keep default export for backward compatibility
export default lightTheme;
